/**
 * cbamUnits.js
 * Single source of truth for all unit labels and dropdown options used across
 * Forms A–F and the Summary sheets in our CBAM application.
 *
 * Derived from the official EU CBAM Communication Template V2.1
 *   File:   2_CBAM_SEE_V2_1_Example_Steel_1_Blast_furnace_final.xlsx
 *   Sheet:  Parameters_Constants
 *   Rows:   24–53 define every canonical unit constant.
 *
 * If the EU publishes a newer version of the template, update this file ONCE
 * and every form will pick up the change automatically.
 */

// ─── 1. Atomic unit constants ────────────────────────────────────────────────
// These mirror the CONST_* names in the EU workbook so that any reviewer can
// trace a unit back to the official spec sheet.

export const UNITS = {
  // Mass & volume
  T:          "t",              // CONST_t          — tonnes
  KNM3:       "1000Nm3",        // CONST_kNm3       — thousand normal m³
  TONNES:     "tonnes",         // CONST_tonnes

  // Energy
  TJ:         "TJ",             // CONST_TJ
  GJ:         "GJ",             // CONST_GJ
  GJ_PER_T:   "GJ/t",           // CONST_GJpt       — Net Calorific Value (solid/liquid)
  GJ_PER_KNM3:"GJ/1000Nm3",     // CONST_GJpkNm3    — NCV for gases

  // Electricity
  MWH:        "MWh",            // CONST_MWh
  GWH:        "GWh",            // CONST_GWh

  // Emissions
  TCO2:       "tCO2",           // CONST_tCO2
  TCO2E:      "tCO2e",          // CONST_tCO2eq    — CO₂ equivalent (SEE columns)
  TCO2_PER_T: "tCO2/t",         // CONST_tCO2pt
  TCO2_PER_TJ:"tCO2/TJ",        // CONST_tCO2pTJ
  TCO2_PER_KNM3:"tCO2/1000Nm3", // CONST_tCO2pkNm3
  TCO2_PER_MWH:"tCO2/MWh",      // electricity EF

  // Carbon content
  TC:         "tC",             // CONST_tC
  TC_PER_T:   "tC/t",           // CONST_tCpt
  TC_PER_KNM3:"tC/1000Nm3",     // CONST_tCpkNm3

  // SEE — specific embedded emissions
  TCO2E_PER_T:    "tCO2e/t",
  TCO2E_PER_KNM3: "tCO2e/1000Nm3",
  MWH_PER_T:      "MWh/t",        // specific electricity consumption

  // Flow / concentration / time
  G_PER_NM3:    "g/Nm3",        // CONST_gpNm3
  KNM3_PER_H:   "1000Nm3/h",    // CONST_Nm3ph
  KNM3_PER_YR:  "1000Nm3/Year", // CONST_kNm3pa
  H_PER_PERIOD: "h/period",     // CONST_hpa

  // Misc
  PERCENT:    "%",
  NA:         "n.a.",           // CONST_NA
};


// ─── 2. Composite dropdown groups ────────────────────────────────────────────
// These are the EU-defined "select one of these" lists. Use these for true
// user-selectable dropdowns (the 6 dropdown spots identified across Forms A–F).
//
// Each entry is { value, label } so it plugs straight into MUI <Select> /
// native <select> / Autocomplete without further mapping.

export const UNIT_GROUPS = {
  // CONST_torkNm3 — used for activity data, production levels (Form B/D/E)
  MASS_OR_VOLUME: [
    { value: UNITS.T,     label: "t (tonnes)" },
    { value: UNITS.KNM3,  label: "1000Nm³ (thousand normal m³)" },
  ],

  // CONST_torkNm3orNA — same plus N/A
  MASS_OR_VOLUME_OR_NA: [
    { value: UNITS.T,     label: "t (tonnes)" },
    { value: UNITS.KNM3,  label: "1000Nm³ (thousand normal m³)" },
    { value: UNITS.NA,    label: "n.a." },
  ],

  // CONST_GJpt / CONST_GJpkNm3 — NCV unit (Form B column I)
  NCV: [
    { value: UNITS.GJ_PER_T,    label: "GJ/t (solid / liquid fuels)" },
    { value: UNITS.GJ_PER_KNM3, label: "GJ/1000Nm³ (gaseous fuels)" },
  ],

  // CONST_tCO2pTJOrtCO2pt — Emission Factor unit (Form B column K)
  EMISSION_FACTOR: [
    { value: UNITS.TCO2_PER_TJ,   label: "tCO2/TJ (energy-based)" },
    { value: UNITS.TCO2_PER_T,    label: "tCO2/t (mass-based)" },
    { value: UNITS.TCO2_PER_KNM3, label: "tCO2/1000Nm³ (volume-based)" },
  ],

  // CONST_tCptOrtCpkNm3 — Carbon content unit (Form B column M)
  CARBON_CONTENT: [
    { value: UNITS.TC_PER_T,    label: "tC/t (mass-based)" },
    { value: UNITS.TC_PER_KNM3, label: "tC/1000Nm³ (volume-based)" },
    { value: UNITS.NA,          label: "n.a." },
  ],

  // Currency for Form F (carbon price)
  CURRENCY: [
    { value: "EUR", label: "EUR — Euro" },
    { value: "USD", label: "USD — US Dollar" },
    { value: "GBP", label: "GBP — British Pound" },
    { value: "CNY", label: "CNY — Chinese Yuan" },
    { value: "INR", label: "INR — Indian Rupee" },
    { value: "TRY", label: "TRY — Turkish Lira" },
    { value: "RUB", label: "RUB — Russian Ruble" },
    { value: "JPY", label: "JPY — Japanese Yen" },
    { value: "KRW", label: "KRW — South Korean Won" },
    // extend as needed — ISO 4217
  ],
};


// ─── 3. Helper: pick the right "per unit" based on aggregated good ──────────
// Special case from the workbook: when the good is electricity, the base unit
// is MWh rather than t. Everything else defaults to t (or 1000Nm³ for gases).

export const getBaseUnitForGood = (aggregatedGoodCategory = "") => {
  const cat = aggregatedGoodCategory.toLowerCase();
  if (cat.includes("electricity")) return UNITS.MWH;
  if (cat.includes("hydrogen"))    return UNITS.KNM3; // gaseous good
  return UNITS.T;
};

// Specific-emissions unit follows the base unit
export const getSpecificEmissionsUnitForGood = (aggregatedGoodCategory = "") => {
  const base = getBaseUnitForGood(aggregatedGoodCategory);
  if (base === UNITS.MWH)  return "tCO2e/MWh";
  if (base === UNITS.KNM3) return UNITS.TCO2E_PER_KNM3;
  return UNITS.TCO2E_PER_T;
};


// ─── 4. Per-field unit map ───────────────────────────────────────────────────
// Lookup table: given a form code and field key, return either:
//   { type: "label",    unit: "MWh" }                       — fixed display label
//   { type: "dropdown", options: UNIT_GROUPS.EMISSION_FACTOR } — user-selectable
//
// Forms reference this so individual components don't hard-code unit strings.

export const FIELD_UNITS = {
  // ── Form A (Installation Data) ──
  // No unit fields — pure metadata.
  A: {},

  // ── Form B (Emissions at installation level) ──
  B: {
    ncv:               { type: "dropdown", options: UNIT_GROUPS.NCV },
    emissionFactor:    { type: "dropdown", options: UNIT_GROUPS.EMISSION_FACTOR },
    carbonContent:     { type: "dropdown", options: UNIT_GROUPS.CARBON_CONTENT },
    oxidationFactor:   { type: "label",    unit: UNITS.PERCENT },
    conversionFactor:  { type: "label",    unit: UNITS.PERCENT },
    biomassContent:    { type: "label",    unit: UNITS.PERCENT },
    nonSustBiomass:    { type: "label",    unit: UNITS.PERCENT },
    hourlyGHGConc:     { type: "label",    unit: UNITS.G_PER_NM3 },
    operatingHours:    { type: "label",    unit: UNITS.H_PER_PERIOD },
    flueGasFlow:       { type: "label",    unit: UNITS.KNM3_PER_H },
    activityData:      { type: "dropdown", options: UNIT_GROUPS.MASS_OR_VOLUME },
  },

  // ── Form C (Emissions & Energy summary) ──
  C: {
    fuelBalance:       { type: "label", unit: UNITS.TJ },
    totalDirect:       { type: "label", unit: UNITS.TCO2E },
    totalIndirect:     { type: "label", unit: UNITS.TCO2E },
    totalPFC:          { type: "label", unit: UNITS.TCO2E },
    measurementBased:  { type: "label", unit: UNITS.TCO2E },
    otherEmissions:    { type: "label", unit: UNITS.TCO2E },
    totalEmissions:    { type: "label", unit: UNITS.TCO2E },
  },

  // ── Form D (Production processes) ──
  D: {
    directAttribEm:    { type: "label", unit: UNITS.TCO2E },        // DAE
    heatImported:      { type: "label", unit: UNITS.TJ },
    heatExported:      { type: "label", unit: UNITS.TJ },
    heatEF:            { type: "label", unit: UNITS.TCO2_PER_TJ },
    wasteGas:          { type: "label", unit: UNITS.TJ },
    wasteGasEF:        { type: "label", unit: UNITS.TCO2_PER_TJ },
    electricityConsumption: { type: "label", unit: UNITS.MWH },
    electricityEF:     { type: "label", unit: UNITS.TCO2_PER_MWH },
    electricityExported: { type: "label", unit: UNITS.MWH },
    // Production levels: unit depends on aggregated good — use getBaseUnitForGood()
    productionLevels:  { type: "dynamic", resolver: getBaseUnitForGood },
    producedForMarket: { type: "dynamic", resolver: getBaseUnitForGood },
    consumedInOther:   { type: "dynamic", resolver: getBaseUnitForGood },
  },

  // ── Form E (Purchased precursors) ──
  E: {
    purchasedLevels:   { type: "dynamic", resolver: getBaseUnitForGood },
    consumedInProcess: { type: "dynamic", resolver: getBaseUnitForGood },
    consumedOther:     { type: "dynamic", resolver: getBaseUnitForGood },
    seeDirect:         { type: "dynamic", resolver: getSpecificEmissionsUnitForGood },
    specElecConsumption: { type: "label", unit: UNITS.MWH_PER_T },
    electricityEF:     { type: "label", unit: UNITS.TCO2_PER_MWH },
    seeIndirect:       { type: "dynamic", resolver: getSpecificEmissionsUnitForGood },
  },

  // ── Form F (Carbon price & CHP) ──
  F: {
    chpFuelInput:      { type: "label", unit: UNITS.TJ },
    chpHeatOutput:     { type: "label", unit: UNITS.TJ },
    chpElecOutput:     { type: "label", unit: UNITS.MWH },
    chpEmissions:      { type: "label", unit: UNITS.TCO2 },
    chpEF:             { type: "label", unit: UNITS.TCO2_PER_TJ },
    currency:          { type: "dropdown", options: UNIT_GROUPS.CURRENCY },
    // CP due / rebate amount unit = `${currency}/t` or `${currency}/MWh`
    // — compose at render time using currency + getBaseUnitForGood().
    carbonPriceDue:    { type: "composed", parts: ["currency", "/", "baseUnit"] },
    rebateAmount:      { type: "composed", parts: ["currency", "/", "baseUnit"] },
  },
};


// ─── 5. Convenience getter ───────────────────────────────────────────────────
// Usage in a form:
//   const unitInfo = getFieldUnit("B", "emissionFactor");
//   if (unitInfo.type === "dropdown") { render <Select options={unitInfo.options} /> }
//   else                              { render <span>{unitInfo.unit}</span> }

export const getFieldUnit = (formCode, fieldKey, context = {}) => {
  const info = FIELD_UNITS[formCode]?.[fieldKey];
  if (!info) return { type: "label", unit: "" };

  if (info.type === "dynamic") {
    return { type: "label", unit: info.resolver(context.aggregatedGood) };
  }
  if (info.type === "composed") {
    const currency  = context.currency || "EUR";
    const baseUnit  = getBaseUnitForGood(context.aggregatedGood);
    return { type: "label", unit: `${currency}/${baseUnit}` };
  }
  return info;
};


export default {
  UNITS,
  UNIT_GROUPS,
  FIELD_UNITS,
  getBaseUnitForGood,
  getSpecificEmissionsUnitForGood,
  getFieldUnit,
};